<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-6">
					<h3 class="page-header">
						<i class="fa fa-bar-chart animated bounceInDown show-info"></i>
						Attendance Reports
					</h3>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<div class="row stock_section">
				<div class="col-md-6">
					<label>Month</label>
					<div class="flex-row" style="display:flex;align-items:center">
						<button class="btn btn-default" @click="shiftMonth(-1)">
							<i class="fa fa-chevron-left"></i>
						</button>
						<input
							type="month"
							class="form-control form-cascade-control input-box-short"
							style="margin:0 8px"
							v-model="month"
							@change="refresh"
						/>
						<button class="btn btn-default" @click="shiftMonth(1)">
							<i class="fa fa-chevron-right"></i>
						</button>
						<span class="text-muted" style="margin-left:10px" v-if="period">
							{{ formatDate(period.from) }} – {{ formatDate(period.to) }}
						</span>
						<span v-if="isPeriodLocked" class="badge bg-secondary" style="margin-left:10px">
							<i class="fa fa-lock"></i> Locked
						</span>
					</div>
				</div>
				<div class="col-md-6 text-right">
					<label class="d-block">&nbsp;</label>
					<div class="btn-group" style="margin-right:6px">
						<button
							class="btn"
							:class="view === 'grid' ? 'btn-primary' : 'btn-default'"
							@click="view = 'grid'"
						>
							<i class="fa fa-th"></i> Month grid
						</button>
						<button
							class="btn"
							:class="view === 'summary' ? 'btn-primary' : 'btn-default'"
							@click="view = 'summary'"
						>
							<i class="fa fa-list"></i> Summary
						</button>
					</div>
					<button class="btn btn-primary" :disabled="downloading" @click="downloadCsv">
						<i class="fa fa-download"></i> Download CSV
					</button>
					<button
						class="btn btn-default"
						:disabled="loading || loadingDays"
						:title="loadingDays ? 'Wait for the daily times to finish loading' : 'Print on A4, landscape'"
						@click="printReport"
					>
						<i class="fa fa-print"></i> Print
					</button>
					<button v-if="!isPeriodLocked" class="btn btn-default" @click="$modal.show('attendance-lock')">
						<i class="fa fa-lock"></i> Lock period
					</button>
					<button v-else class="btn btn-default" @click="unlockPeriod">
						<i class="fa fa-unlock"></i> Unlock
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<div class="row stock_section">
				<div class="col-md-4">
					<label>Search</label>
					<input
						type="text"
						class="form-control form-cascade-control input-box-short"
						placeholder="Search name or code"
						v-model="search"
					/>
				</div>
			</div>
		</div>

		<div class="col-md-12" style="margin-top:10px">
			<span class="badge bg-success totals-badge">Present {{ totals.present }}</span>
			<span class="badge bg-danger totals-badge">Absent {{ totals.absent }}</span>
			<span class="badge bg-dark totals-badge">Payable days {{ totals.payable }}</span>
			<span class="badge bg-danger totals-badge">LOP days {{ totals.lop }}</span>
			<span class="badge bg-warning text-dark totals-badge">Overtime {{ totals.ot }}h</span>
			<span v-if="loadingDays" class="text-muted" style="margin-left:6px">
				<i class="fa fa-spinner fa-spin"></i>
				Loading daily times… {{ daysLoaded }} of {{ daysTotal }}
			</span>
			<span v-else-if="dayError" class="text-warning" style="margin-left:6px">
				<i class="fa fa-exclamation-triangle"></i> {{ dayError }}
			</span>
		</div>

		<!-- Everything inside is what Print puts on paper; the toolbar above is not. -->
		<div ref="printArea" class="attendance-print-area">
		<div class="print-only print-head">
			<div>
				<strong class="print-title">Attendance · {{ monthTitle }}</strong>
				<span v-if="period">{{ formatDate(period.from) }} – {{ formatDate(period.to) }}</span>
				<span v-if="isPeriodLocked"> · Locked</span>
				<span v-if="search.trim()"> · Filtered: “{{ search.trim() }}”</span>
			</div>
			<div>
				Present {{ totals.present }} · Absent {{ totals.absent }} · Payable {{ totals.payable }} ·
				LOP {{ totals.lop }} · OT {{ totals.ot }}h · Printed {{ printedAt }}
			</div>
		</div>

		<div class="col-md-12" v-if="error" style="margin-top:10px">
			<div class="alert alert-danger">{{ error }}</div>
		</div>

		<!-- Month grid: one column per day, in over out, totals at the end. -->
		<div
			class="col-md-12 table-responsive"
			style="padding:0px;margin-top:10px"
			v-else-if="view === 'grid'"
		>
			<div class="panel">
				<div class="panel-body table" style="overflow-x:auto;padding-left:0px">
					<table class="table table-bordered month-grid">
						<thead class="table-font">
							<tr>
								<th class="sticky-col">Employee</th>
								<th
									v-for="d in dates"
									:key="d.date"
									class="day-head"
									:class="{ 'is-weekend': d.is_weekend }"
								>
									<div class="day-num">{{ d.day }}</div>
									<small class="text-muted">{{ d.weekday }}</small>
								</th>
								<th class="total-col">Present</th>
								<th class="total-col">Absent</th>
							</tr>
						</thead>
						<tbody>
							<tr v-if="loading">
								<td :colspan="dates.length + 3" class="text-center">Loading…</td>
							</tr>
							<tr v-else-if="!filteredRows.length">
								<td :colspan="dates.length + 3" class="text-center">
									Nothing computed for this period yet.
								</td>
							</tr>
							<template v-else>
							<tr v-for="row in filteredRows" :key="row.employee_id">
								<td class="sticky-col">
									<a href="javascript:void(0)" class="text-primary" @click="openEmployee(row)">
										<strong>{{ row.name }}</strong>
									</a>
									<br />
									<small class="text-muted">
										{{ row.employee_code || '' }}{{ row.policy ? ' · ' + row.policy : '' }}
									</small>
								</td>
								<td
									v-for="d in dates"
									:key="d.date"
									class="day-cell"
									:class="cellClass(row, d)"
									:title="cellTitle(row, d)"
								>
									<template v-if="hasTimes(row, d)">
										<span class="t-in" :class="{ late: dayOf(row, d).is_late }">
											{{ dayOf(row, d).in || '—' }}
										</span>
										<span class="t-out">{{ dayOf(row, d).out || '—' }}</span>
									</template>
									<span v-else class="code">{{ shortStatus(row, d) }}</span>
								</td>
								<td class="total-col">
									<strong class="text-success">{{ row.present }}</strong>
									<small v-if="row.half_day" class="text-muted d-block">+{{ row.half_day }} HD</small>
								</td>
								<td class="total-col">
									<strong :class="Number(row.absent) > 0 ? 'text-danger' : ''">{{ row.absent }}</strong>
									<small v-if="row.lwp" class="text-muted d-block">+{{ row.lwp }} LWP</small>
								</td>
							</tr>
							</template>
						</tbody>
					</table>
				</div>
			</div>
			<div style="padding:6px 2px">
				<small class="text-muted">
					Top time is the first punch in, bottom the last punch out. A late arrival is shown in amber.
					P present · HD half day · A absent · WO weekly off · H holiday · L leave · LWP leave without
					pay · MP missing punch · WFH work from home · OD on duty · CO comp off · – not computed.
				</small>
			</div>
		</div>

		<!-- The payroll summary, one row per employee. -->
		<div class="col-md-12 table-responsive" style="padding:0px;margin-top:10px" v-else>
			<div class="panel">
				<div class="panel-body table" style="overflow-x:auto;padding-left:0px">
					<table class="table table-bordered table-hover table-striped display summary-table">
						<thead class="table-font">
							<th>Employee</th>
							<th>Present</th>
							<th>Half</th>
							<th>Absent</th>
							<th>Off</th>
							<th>Holiday</th>
							<th>Leave</th>
							<th>LWP</th>
							<th>Missing</th>
							<th>Late</th>
							<th>Hours</th>
							<th>OT h</th>
							<th>Payable</th>
							<th>LOP</th>
						</thead>
						<tbody>
							<tr v-if="loading">
								<td colspan="14" class="text-center">Loading…</td>
							</tr>
							<tr v-else-if="!filteredRows.length">
								<td colspan="14" class="text-center">Nothing computed for this period yet.</td>
							</tr>
							<tr v-for="row in filteredRows" :key="row.employee_id">
								<td>
									<strong>{{ row.name }}</strong>
									<br />
									<small class="text-muted">
										{{ row.employee_code || '' }}{{ row.policy ? ' · ' + row.policy : '' }}
									</small>
								</td>
								<td>{{ row.present }}</td>
								<td>{{ row.half_day }}</td>
								<td>{{ row.absent }}</td>
								<td>{{ row.weekly_off }}</td>
								<td>{{ row.holiday }}</td>
								<td>{{ row.on_leave }}</td>
								<td>{{ row.lwp }}</td>
								<td>{{ row.missing_punch }}</td>
								<td>{{ row.late_marks }}</td>
								<td>{{ row.worked_hours }}</td>
								<td>{{ row.ot_hours }}</td>
								<td>
									<strong>{{ row.payable_days }}</strong>
								</td>
								<td>
									<span :class="{ 'text-danger font-weight-bold': Number(row.lop_days) > 0 }">
										{{ row.lop_days }}
									</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
		</div>

		<modal name="attendance-lock" :width="480" :height="'auto'" :adaptive="true">
			<div style="padding:18px">
				<h4 class="modal-header-title">Lock this period</h4>
				<div class="alert alert-warning">
					Locking stops these days being recomputed, so the numbers payroll takes cannot shift
					afterwards. It can be undone.
				</div>
				<p v-if="period">{{ formatDate(period.from) }} – {{ formatDate(period.to) }}</p>
				<div class="form-group">
					<input type="text" class="form-control form-cascade-control" placeholder="Note (optional)" v-model="lockNote" />
				</div>
				<div class="text-right" style="margin-top:12px">
					<button class="btn btn-default" @click="$modal.hide('attendance-lock')">Cancel</button>
					<button class="btn btn-primary" :disabled="locking" @click="lockPeriod">Lock period</button>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import { registerService, reportsService, errorMessage } from './attendanceService'
import attendanceFormat from './attendanceFormat'

/** Compact codes for the grid — a full label will not fit a day column. */
const SHORT_STATUS = {
	present: 'P',
	half_day: 'HD',
	absent: 'A',
	weekly_off: 'WO',
	holiday: 'H',
	on_leave: 'L',
	lwp: 'LWP',
	wfh: 'WFH',
	on_duty: 'OD',
	comp_off: 'CO',
	missing_punch: 'MP',
}

export default {
	name: 'AttendanceMonthlyReport',
	mixins: [attendanceFormat],
	data() {
		return {
			month: this.currentMonth(),
			view: 'grid',
			search: '',
			data: null,
			locks: [],
			loading: false,
			error: '',
			// employeeId|date -> { status, in, out, is_late }, filled from the
			// daily register when the monthly report carries totals only.
			dayMap: {},
			dayCache: {},
			daysLoaded: 0,
			daysTotal: 0,
			dayError: '',
			requestToken: 0,
			lockNote: '',
			locking: false,
			downloading: false,
			printedAt: '',
		}
	},
	computed: {
		monthTitle() {
			const parts = this.month.split('-').map(Number)
			return new Date(parts[0], parts[1] - 1, 1).toLocaleDateString(undefined, {
				month: 'long',
				year: 'numeric',
			})
		},
		rows() {
			return (this.data && this.data.rows) || []
		},
		/** The monthly report already carries per-day punches (newer API). */
		reportHasDays() {
			return this.rows.some((row) => row && row.days)
		},
		loadingDays() {
			return this.daysTotal > 0 && this.daysLoaded < this.daysTotal
		},
		filteredRows() {
			const term = this.search.trim().toLowerCase()
			if (!term) return this.rows
			return this.rows.filter((row) =>
				`${row.name || ''} ${row.employee_code || ''}`.toLowerCase().includes(term)
			)
		},
		/**
		 * Grid columns. The API sends them once it knows the period; until then
		 * (and on an older API) they are derived here.
		 */
		dates() {
			if (this.data && this.data.dates && this.data.dates.length) return this.data.dates
			if (this.period) return this.rangeDates(this.period.from, this.period.to)
			const parts = this.month.split('-').map(Number)
			const last = new Date(parts[0], parts[1], 0).getDate()
			return this.rangeDates(`${this.month}-01`, `${this.month}-${String(last).padStart(2, '0')}`)
		},
		period() {
			return this.data && this.data.period
		},
		/** The period the report covers is already locked for payroll. */
		isPeriodLocked() {
			if (!this.period || !this.locks) return false
			return this.locks.some(
				(lock) => lock.period_start === this.period.from && lock.period_end === this.period.to
			)
		},
		activeLock() {
			if (!this.period) return null
			return this.locks.find(
				(lock) => lock.period_start === this.period.from && lock.period_end === this.period.to
			)
		},
		totals() {
			const rows = this.filteredRows
			const sum = (key) => rows.reduce((total, r) => total + Number(r[key] || 0), 0)
			return {
				present: sum('present'),
				absent: sum('absent'),
				payable: sum('payable_days').toFixed(2),
				lop: sum('lop_days').toFixed(2),
				ot: sum('ot_hours').toFixed(2),
			}
		},
	},
	mounted() {
		this.loadLocks()
		this.refresh()
	},
	methods: {
		refresh() {
			this.loading = true
			this.error = ''
			this.dayMap = {}
			this.daysLoaded = 0
			this.daysTotal = 0
			this.dayError = ''
			const token = ++this.requestToken
			reportsService
				.monthly(this.month)
				.then((data) => {
					this.data = data
					this.loadDays(token)
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load the report.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		loadLocks() {
			registerService
				.locks()
				.then((data) => {
					this.locks = data || []
				})
				.catch(() => {})
		},
		shiftMonth(delta) {
			const parts = this.month.split('-').map(Number)
			const date = new Date(parts[0], parts[1] - 1 + delta, 1)
			this.month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
			this.refresh()
		},

		// -------------------------------------------------------------------
		// The month grid
		// -------------------------------------------------------------------

		/** One column descriptor per date in an inclusive range. */
		rangeDates(fromIso, toIso) {
			const parse = (iso) => {
				const parts = String(iso).slice(0, 10).split('-').map(Number)
				return new Date(parts[0], parts[1] - 1, parts[2])
			}
			const cursor = parse(fromIso)
			const end = parse(toIso)
			const out = []
			while (cursor <= end && out.length < 62) {
				out.push({
					date: this.toIsoDate(cursor),
					day: cursor.getDate(),
					weekday: cursor.toLocaleDateString(undefined, { weekday: 'short' }),
					is_weekend: cursor.getDay() === 0 || cursor.getDay() === 6,
				})
				cursor.setDate(cursor.getDate() + 1)
			}
			return out
		},
		/**
		 * Fill the grid when the monthly report carries totals only.
		 *
		 * Two endpoints can supply it: one request per date (covers everyone) or
		 * one per employee (covers the whole month). The API allows 60 calls a
		 * minute, so take whichever axis is shorter — a 12-person company costs
		 * 12 requests, not 30. Per-employee only works when the period sits
		 * inside one calendar month, which is what that endpoint accepts.
		 */
		loadDays(token) {
			if (this.reportHasDays) return

			const cached = this.dayCache[this.month]
			if (cached) {
				this.dayMap = cached
				this.daysTotal = 0
				return
			}

			const dates = this.dates.map((d) => d.date)
			if (!dates.length) return

			const oneCalendarMonth = dates[0].slice(0, 7) === dates[dates.length - 1].slice(0, 7)
			const byEmployee =
				oneCalendarMonth && this.rows.length > 0 && this.rows.length < dates.length

			const map = {}
			const queue = byEmployee
				? this.rows.map((row) => () => this.fetchEmployee(row, map))
				: dates.map((date) => () => this.fetchDate(date, map))

			this.daysTotal = queue.length
			this.daysLoaded = 0

			// A few in flight keeps a month under a second without spending the
			// whole per-minute budget at once.
			const CONCURRENCY = 3

			const worker = () => {
				if (token !== this.requestToken) return Promise.resolve()
				const job = queue.shift()
				if (!job) return Promise.resolve()
				return job()
					.catch((e) => {
						this.dayError = errorMessage(e, 'Some days could not be loaded.')
					})
					.then(() => {
						if (token !== this.requestToken) return null
						this.daysLoaded += 1
						return worker()
					})
			}

			Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker)).then(() => {
				if (token !== this.requestToken) return
				this.dayMap = map
				if (!this.dayError) this.$set(this.dayCache, this.month, map)
			})
		},
		/** Everyone's verdict for one date. */
		fetchDate(date, map) {
			return this.withRetry(() => registerService.daily({ date })).then((payload) => {
				const entries = (payload && payload.rows) || []
				entries.forEach((entry) => {
					this.putCell(map, entry.employee_id, date, entry.day)
				})
			})
		},
		/** One employee's whole month. */
		fetchEmployee(row, map) {
			return this.withRetry(() =>
				registerService.employeeMonth(row.employee_id, this.month)
			).then((payload) => {
				const days = (payload && payload.days) || []
				days.forEach((day) => {
					this.putCell(map, row.employee_id, String(day.work_date).slice(0, 10), day)
				})
			})
		},
		putCell(map, employeeId, date, day) {
			if (!day) return
			if (day.status === 'not_computed' && !day.first_in && !day.last_out) return
			const cell = {
				status: day.status,
				in: day.first_in || null,
				out: day.last_out || null,
				is_late: !!day.is_late,
			}
			map[`${employeeId}|${date}`] = cell
			// Show each column as it lands rather than after the whole month.
			this.$set(this.dayMap, `${employeeId}|${date}`, cell)
		},
		/**
		 * Retry a throttled call. Laravel says how long to wait; guessing shorter
		 * just burns another slot, so honour Retry-After.
		 */
		withRetry(call, attempt = 0) {
			return call().catch((e) => {
				const response = e && e.response
				if (response && response.status === 429 && attempt < 2) {
					const advertised = Number(response.headers && response.headers['retry-after'])
					const wait = Math.min(Number.isFinite(advertised) ? advertised : 5, 30) * 1000
					return new Promise((resolve) => setTimeout(resolve, wait)).then(() =>
						this.withRetry(call, attempt + 1)
					)
				}
				throw e
			})
		},
		dayOf(row, column) {
			if (row.days && row.days[column.date]) return row.days[column.date]
			return this.dayMap[`${row.employee_id}|${column.date}`] || null
		},
		hasTimes(row, column) {
			const day = this.dayOf(row, column)
			return !!(day && (day.in || day.out))
		},
		shortStatus(row, column) {
			const day = this.dayOf(row, column)
			if (!day) return '–'
			return SHORT_STATUS[day.status] || '–'
		},
		cellClass(row, column) {
			const day = this.dayOf(row, column)
			return [day ? `st-${day.status}` : 'st-none', { 'is-weekend': column.is_weekend }]
		},
		cellTitle(row, column) {
			const day = this.dayOf(row, column)
			const label = day ? this.statusLabel(day.status) : 'Not computed'
			const times = day && (day.in || day.out) ? ` · in ${day.in || '—'}, out ${day.out || '—'}` : ''
			return `${row.name} · ${this.formatDate(column.date)} · ${label}${times}`
		},
		openEmployee(row) {
			this.$router.push({
				name: 'attendance-employee-month',
				params: { employeeId: row.employee_id },
				query: { month: this.month },
			})
		},

		// -------------------------------------------------------------------
		// Locking and export
		// -------------------------------------------------------------------

		lockPeriod() {
			if (!this.period) return
			this.locking = true
			registerService
				.lock({
					period_start: this.period.from,
					period_end: this.period.to,
					note: this.lockNote || undefined,
				})
				.then(() => {
					this.$notify({
						text: 'Period locked: these days can no longer be recomputed.',
						type: 'success',
					})
					this.$modal.hide('attendance-lock')
					this.lockNote = ''
					this.loadLocks()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Failed'), type: 'error' })
				})
				.finally(() => {
					this.locking = false
				})
		},
		unlockPeriod() {
			if (!this.activeLock) return
			if (
				!window.confirm(
					'Unlocking lets these days be recomputed again, which can change numbers payroll has already used. Continue?'
				)
			)
				return
			registerService
				.unlock(this.activeLock.id)
				.then(() => {
					this.$notify({ text: 'Unlocked', type: 'success' })
					this.loadLocks()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Failed'), type: 'error' })
				})
		},
		downloadCsv() {
			return this.view === 'grid' ? this.downloadGridCsv() : this.downloadSummaryCsv()
		},
		/**
		 * Print the current view (grid or summary, search applied) on A4 landscape.
		 *
		 * Only a copy of the report is printed: it is placed directly under
		 * <body> and everything else is hidden, so the app's sidebar and header
		 * cannot push the table onto extra pages. The landscape @page rule is
		 * added for this print only — left in place it would turn every other
		 * printout in the app landscape too.
		 */
		printReport() {
			this.printedAt = new Date().toLocaleString(undefined, {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			})

			this.$nextTick(() => {
				const source = this.$refs.printArea
				if (!source) return

				const root = document.createElement('div')
				root.id = 'attendance-print-root'
				root.className = `print-view-${this.view}`
				root.innerHTML = source.outerHTML
				document.body.appendChild(root)

				const pageRule = document.createElement('style')
				pageRule.textContent =
					'@page { size: A4 landscape; margin: 6mm; ' +
					'@bottom-right { content: "Page " counter(page) " of " counter(pages); font-size: 7pt; } }'
				document.head.appendChild(pageRule)
				document.body.classList.add('attendance-printing')

				const cleanup = () => {
					window.removeEventListener('afterprint', cleanup)
					document.body.classList.remove('attendance-printing')
					root.remove()
					pageRule.remove()
				}
				window.addEventListener('afterprint', cleanup)
				window.print()
			})
		},
		/** The grid is assembled in the browser, so its CSV is built here too. */
		downloadGridCsv() {
			const escape = (value) => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`
			const header = ['Employee Code', 'Name', 'Policy']
				.concat(this.dates.map((d) => `${d.day} ${d.weekday}`))
				.concat(['Present', 'Absent'])

			const lines = [header.map(escape).join(',')]
			this.filteredRows.forEach((row) => {
				const cells = this.dates.map((d) => {
					const day = this.dayOf(row, d)
					if (!day) return '-'
					if (day.in || day.out) return `${day.in || '-'} / ${day.out || '-'}`
					return SHORT_STATUS[day.status] || '-'
				})
				lines.push(
					[row.employee_code || '', row.name || '', row.policy || '']
						.concat(cells)
						.concat([row.present, row.absent])
						.map(escape)
						.join(',')
				)
			})

			const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
			const url = URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = url
			link.download = `attendance-grid-${this.month}.csv`
			link.click()
			URL.revokeObjectURL(url)
		},
		/** Downloading needs the bearer token, so fetch as a blob rather than link out. */
		downloadSummaryCsv() {
			this.downloading = true
			window.axios
				.get('api/attendance/admin/reports/monthly.csv', {
					params: { month: this.month },
					responseType: 'blob',
				})
				.then((response) => {
					const url = URL.createObjectURL(response.data)
					const link = document.createElement('a')
					link.href = url
					link.download = `attendance-${this.month}.csv`
					link.click()
					URL.revokeObjectURL(url)
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Download failed'), type: 'error' })
				})
				.finally(() => {
					this.downloading = false
				})
		},
	},
}
</script>

<style scoped>
.modal-header-title {
	margin-top: 0;
}
/* Shown only on the printed copy (see the unscoped print styles below). */
.print-only {
	display: none;
}
.totals-badge {
	margin-right: 6px;
	margin-bottom: 6px;
	display: inline-block;
	font-size: 12px;
}

/*
 * The app pins .btn-primary to a fixed width, which clipped these labels. The
 * toolbar has room, so let them size to their text instead of wrapping mid-word.
 */
.stock_section .btn {
	white-space: nowrap;
	width: auto;
}

.month-grid {
	font-size: 11px;
	border-collapse: separate;
	border-spacing: 0;
}
.month-grid th,
.month-grid td {
	padding: 3px 4px;
	vertical-align: middle;
	white-space: nowrap;
}

/* The name has to stay readable while the days scroll past it. */
.month-grid .sticky-col {
	position: sticky;
	left: 0;
	z-index: 2;
	background: #fff;
	min-width: 165px;
	box-shadow: 1px 0 0 #ddd;
}
.month-grid thead .sticky-col {
	z-index: 3;
	background: #f5f7fa;
}

.month-grid thead th {
	/* The app paints table headers white; these cells are tinted, not dark. */
	color: #333;
}
.month-grid .day-head {
	text-align: center;
	min-width: 52px;
	background: #f5f7fa;
}
.month-grid .day-num {
	font-weight: 600;
	font-size: 12px;
}
.month-grid .day-head.is-weekend,
.month-grid .day-cell.is-weekend {
	background: #f0f2f5;
}

.month-grid .day-cell {
	text-align: center;
	line-height: 1.25;
}
.month-grid .t-in,
.month-grid .t-out {
	display: block;
}
.month-grid .t-in {
	font-weight: 600;
	color: #1b5e20;
}
.month-grid .t-in.late {
	color: #b26a00;
}
.month-grid .t-out {
	color: #555;
}
.month-grid .code {
	color: #888;
	font-weight: 600;
}


/* A tint per status, so a month reads at a glance. */
.month-grid .st-present,
.month-grid .st-wfh,
.month-grid .st-on_duty {
	background: #eaf7ed;
}
.month-grid .st-half_day,
.month-grid .st-missing_punch {
	background: #fff5e0;
}
.month-grid .st-absent,
.month-grid .st-lwp {
	background: #fdecea;
}
.month-grid .st-on_leave {
	background: #e7f2fb;
}
.month-grid .st-holiday,
.month-grid .st-comp_off {
	background: #f3ebfa;
}

.month-grid .total-col {
	text-align: center;
	min-width: 62px;
	background: #f5f7fa;
	font-size: 12px;
}
</style>

<!--
	Print: unscoped because the printed copy lives under <body>, outside this
	component. Every rule is keyed to #attendance-print-root or the body class
	set while printing, so nothing here touches any other page or printout.

	A4 landscape leaves about 285mm across. The name takes 30mm, the two totals
	9mm each and the days share the rest, ~7.7mm apiece: room for "09:30" at 7px.
-->
<style>
@media screen {
	#attendance-print-root {
		display: none;
	}
}

@media print {
	body.attendance-printing > *:not(#attendance-print-root) {
		display: none !important;
	}
	body.attendance-printing {
		margin: 0 !important;
		padding: 0 !important;
		background: #fff !important;
	}

	#attendance-print-root {
		display: block;
		color: #000;
		font-family: Arial, Helvetica, sans-serif;
		-webkit-print-color-adjust: exact;
		print-color-adjust: exact;
	}
	#attendance-print-root * {
		-webkit-print-color-adjust: exact;
		print-color-adjust: exact;
	}

	/* Bootstrap's grid, panels and scroll wrappers only get in the way on paper. */
	#attendance-print-root .col-md-12,
	#attendance-print-root .panel,
	#attendance-print-root .panel-body,
	#attendance-print-root .table-responsive {
		margin: 0 !important;
		padding: 0 !important;
		border: 0 !important;
		box-shadow: none !important;
		overflow: visible !important;
		float: none !important;
		width: 100% !important;
	}
	#attendance-print-root a {
		color: #000 !important;
		text-decoration: none !important;
	}
	#attendance-print-root a[href]:after {
		content: none !important;
	}

	#attendance-print-root .print-only {
		display: block !important;
	}
	#attendance-print-root .print-head {
		display: flex !important;
		justify-content: space-between;
		align-items: baseline;
		gap: 8px;
		margin-bottom: 3px;
		font-size: 8px;
	}
	#attendance-print-root .print-title {
		font-size: 12px;
		margin-right: 6px;
	}

	#attendance-print-root table {
		width: 100% !important;
		table-layout: fixed;
		border-collapse: collapse !important;
		margin: 0 !important;
	}
	#attendance-print-root thead {
		display: table-header-group; /* repeat the day header on every page */
	}
	#attendance-print-root tr {
		page-break-inside: avoid;
		break-inside: avoid;
	}
	#attendance-print-root th,
	#attendance-print-root td {
		border: 0.5px solid #9aa3ad !important;
		padding: 1px !important;
		overflow: hidden;
		color: #000;
	}

	/* The month grid, compressed. */
	#attendance-print-root .month-grid {
		font-size: 7px;
	}
	#attendance-print-root .month-grid th,
	#attendance-print-root .month-grid td {
		line-height: 1.15;
	}
	#attendance-print-root .month-grid .sticky-col {
		position: static;
		width: 30mm;
		min-width: 0;
		box-shadow: none;
		white-space: normal;
		text-align: left;
	}
	#attendance-print-root .month-grid .sticky-col strong {
		font-size: 7.5px;
	}
	#attendance-print-root .month-grid .sticky-col small {
		font-size: 6px;
	}
	#attendance-print-root .month-grid .sticky-col br + small {
		display: block;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	#attendance-print-root .month-grid .day-head,
	#attendance-print-root .month-grid .day-cell {
		min-width: 0;
		text-align: center;
	}
	#attendance-print-root .month-grid .day-num {
		font-size: 7.5px;
	}
	#attendance-print-root .month-grid .day-head small {
		font-size: 5.5px;
	}
	#attendance-print-root .month-grid .total-col {
		width: 9mm;
		min-width: 0;
		font-size: 7px;
	}
	#attendance-print-root .month-grid .total-col small {
		font-size: 5.5px;
	}
	/* The legend under the grid. */
	#attendance-print-root .panel + div {
		padding: 2px 0 0 !important;
	}
	#attendance-print-root .panel + div small {
		font-size: 6.5px;
	}

	/* The summary has fourteen columns; it only needs a smaller face. */
	#attendance-print-root .summary-table {
		font-size: 9px;
	}
	#attendance-print-root .summary-table th {
		color: #000 !important;
		background: #eef1f5 !important;
	}
	#attendance-print-root .summary-table th:first-child {
		width: 45mm;
	}
	#attendance-print-root .summary-table td,
	#attendance-print-root .summary-table th {
		padding: 2px 3px !important;
	}
}
</style>
